import { useModals } from 'almanac-ui/Modal'
import { AutopilotUpgradeEntryModal } from 'components/AutopilotUpgradeEntryModal/AutopilotUpgradeEntryModal'
import { AutopilotPlansModal } from 'components/Settings/Workspace/AutopilotPlans/AutopilotPlansModal'
import { useVirtualMarketerPlan } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/hooks/useVirtualMarketerPlan'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef } from 'react'
import { useRootStore } from 'stores/RootStore'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { useCreditUsage } from 'hooks/useCreditUsage'
import { CreditsBalanceModal } from 'blaze-components/CreditUsageDropdown/components/CreditsBalanceModal/CreditsBalanceModal'

export const UpgradeModalListener = observer(() => {
  const workspace = useCurrentWorkspace()
  const { tierUsageStore } = useRootStore()
  const { currentSubscription } = tierUsageStore
  const { openModal } = useModals()
  const hasShownModal = useRef(false)
  const hasShownLowCreditsModal = useRef(false)
  const { data: virtualMarketerPlan } = useVirtualMarketerPlan()
  const { initialized: creditsInitialized, creditUsage, isLowOnCredits, isOutOfCredits } = useCreditUsage()

  const hasCancelledPlan = Boolean(currentSubscription?.status === 'canceled')
  const isPioneerPlan = currentSubscription?.plan === PaymentPlanKey.VirtualMarketer

  useEffect(() => {
    const showForFreemium =
      !hasShownModal.current && workspace?.is_ap_freemium && workspace?.is_ap_freemium_brand_plan_set === false
    const showForPioneerCancelled =
      !hasShownModal.current && hasCancelledPlan && !workspace?.is_ap_freemium && isPioneerPlan && virtualMarketerPlan
    const showForOtherCancelled =
      !hasShownModal.current && hasCancelledPlan && !workspace?.is_ap_freemium && !isPioneerPlan

    if (showForFreemium || showForPioneerCancelled || showForOtherCancelled) {
      const timeoutId = setTimeout(() => {
        if (showForFreemium) {
          openModal(AutopilotUpgradeEntryModal)
        } else if (showForPioneerCancelled) {
          openModal(AutopilotPlansModal)
        } else if (showForOtherCancelled) {
          if (currentSubscription?.trial_ended) {
            openModal(AutopilotUpgradeEntryModal)
          } else {
            openModal(AutopilotPlansModal)
          }
        }
        hasShownModal.current = true
      }, 2000)

      return () => clearTimeout(timeoutId)
    }
  }, [
    workspace?.is_ap_freemium_brand_plan_set,
    workspace?.is_ap_freemium,
    workspace,
    hasCancelledPlan,
    isPioneerPlan,
    virtualMarketerPlan,
    openModal,
    tierUsageStore,
    currentSubscription?.trial_ended,
  ])

  // Show low credits modal once per day per workspace, but not within 24h of onboarding completion
  useEffect(() => {
    if (!creditsInitialized || !currentSubscription || !workspace || !workspace.is_virtual_marketer) return

    const isActiveOrTrialing = currentSubscription.status === 'active' || currentSubscription.status === 'trialing'

    if (!isActiveOrTrialing) return

    const hasLowCredits = isLowOnCredits || isOutOfCredits

    if (!hasLowCredits) return

    const onboardingCompletedAt = workspace.virtual_marketer_onboarding_completed_at
    if (onboardingCompletedAt) {
      const hoursSinceOnboarding = (Date.now() - new Date(onboardingCompletedAt).getTime()) / (1000 * 60 * 60)
      if (hoursSinceOnboarding < 24) return
    }

    const variant: 'low_on_credits' | 'no_credits' = isOutOfCredits ? 'no_credits' : 'low_on_credits'

    const storageKey = `lowCreditsModalLastShown_${workspace.id}_${variant}`
    const lastShownDate = localStorage.getItem(storageKey)
    const today = new Date().toDateString()
    const wasShownToday = lastShownDate === today

    if (!wasShownToday && !hasShownLowCreditsModal.current) {
      const timeoutId = setTimeout(() => {
        openModal(CreditsBalanceModal)
        localStorage.setItem(storageKey, today)
        hasShownLowCreditsModal.current = true
      }, 2000)

      return () => clearTimeout(timeoutId)
    }
  }, [
    creditsInitialized,
    currentSubscription,
    workspace,
    openModal,
    creditUsage,
    isPioneerPlan,
    isLowOnCredits,
    isOutOfCredits,
  ])

  return null // This component doesn't render anything
})
